import React, { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';

/**
 * Confirmation dialog that requires typing the exact school name.
 */
export default function ConfirmSchoolNameDialog({
  open,
  onOpenChange,
  schoolName = '',
  title,
  description,
  confirmLabel = 'Confirm',
  confirmVariant = 'destructive',
  loading = false,
  onConfirm,
}) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (open) setTyped('');
  }, [open, schoolName]);

  const matches = typed.trim() === String(schoolName || '').trim();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              {description}
              <div className="rounded-lg border border-border bg-muted/40 px-3 py-2">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">School</p>
                <p className="font-semibold text-foreground">{schoolName || '—'}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-school-name">
                  Type <span className="font-semibold text-foreground">{schoolName}</span> to confirm
                </Label>
                <Input
                  id="confirm-school-name"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  disabled={loading}
                  className="font-medium"
                  placeholder={schoolName}
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button
            type="button"
            variant={confirmVariant === 'destructive' ? 'destructive' : 'default'}
            className={
              confirmVariant === 'destructive'
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }
            disabled={!matches || loading}
            onClick={(e) => {
              e.preventDefault();
              onConfirm?.();
            }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
